import { useState, useEffect } from 'react'
import { FiClock } from 'react-icons/fi'
import * as FiIcons from 'react-icons/fi'
import { useAuth } from '../../contexts/AuthContext'
import { canAccess, getEffectivePlan } from '../../config/planFeatures'
import toast from 'react-hot-toast'
import { tiposPausaAPI } from '../../services/api'
import { API_URL } from '../../config/api'
import TimelineFichajeV2 from '../../components/TimelineFichajeV2'
import ModalHoraExtra from '../../components/v2/ModalHoraExtra'
import ModalAvisoCorreccionV2 from '../../components/v2/ModalAvisoCorreccionV2'
import ModalValidacionSemanalV2 from '../../components/v2/ModalValidacionSemanalV2'
import { useGeoFichaje } from '../../hooks/useGeoFichaje'
import { useValidacionesPendientes } from '../../hooks/useValidacionesPendientes'
import { PageHeader, Btn } from '../../ui'
import { formatearFecha } from '../../utils/fechas'

/*
 * MiFichaje (V2) — versión rediseñada con DS v2.
 * Misma lógica funcional que el original (state, hooks, callbacks, modales).
 * Cambia solo el lenguaje visual:
 *   - PageHeader v2 con eyebrow numerada
 *   - TimelineFichajeV2 (tarjeta hero rediseñada)
 *   - Modales con ui-card + Btn primitive (sin card-glass / btn-* legacy)
 */

const fetchConAuth = async (url, options = {}, { reintentar = false } = {}) => {
  const token = localStorage.getItem('token')
  if (!token) {
    window.location.href = '/login'
    throw new Error('Sesión no iniciada')
  }

  const ejecutar = async () => {
    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers
      }
    })
    if (response.status === 401) {
      localStorage.removeItem('token')
      window.location.href = '/login'
      throw new Error('Sesión expirada. Redirigiendo al login...')
    }
    return response
  }

  try {
    return await ejecutar()
  } catch (error) {
    if (reintentar && error.message !== 'Sesión expirada. Redirigiendo al login...' && error.name !== 'AbortError') {
      console.warn('⚠️ Error de red en fichaje, reintentando en 2s...', error.message)
      await new Promise(r => setTimeout(r, 2000))
      return await ejecutar()
    }
    throw error
  }
}

// Etiquetas de tipo de ausencia para el aviso de "fichar con permiso aprobado" (B5).
const TIPO_AUSENCIA_LABEL = {
  vacaciones: 'Vacaciones',
  baja_laboral: 'Baja laboral',
  baja_medica: 'Baja médica',
  dia_libre: 'Día libre',
  permiso_retribuido: 'Permiso retribuido',
  asuntos_propios: 'Asuntos propios',
  ausencia_justificada: 'Ausencia justificada',
  otro: 'Permiso'
}

const MiFichajeV2 = () => {
  const { empleado, empresaInfo } = useAuth()
  const [fichajeActivo, setFichajeActivo] = useState(null)
  const [fichajesDelDia, setFichajesDelDia] = useState([])
  const [pausas, setPausas] = useState([])
  const [pausaActiva, setPausaActiva] = useState(null)
  const [tiposPausa, setTiposPausa] = useState([])
  const [loading, setLoading] = useState(true)
  const [procesando, setProcesando] = useState(false)
  const [horaActual, setHoraActual] = useState(new Date())
  const [geolocalizacion, setGeolocalizacion] = useState(null)
  const [mostrarModalPausa, setMostrarModalPausa] = useState(false)
  const [mostrarModalSalida, setMostrarModalSalida] = useState(false)
  const [ultimaPausaCreada, setUltimaPausaCreada] = useState(null)
  const [segundosRestantes, setSegundosRestantes] = useState(0)

  const [modalOlvido, setModalOlvido] = useState(null)
  const [horaOlvidoInput, setHoraOlvidoInput] = useState('')
  const [procesandoOlvido, setProcesandoOlvido] = useState(false)
  // La decisión del modal de olvido (>14h) se guarda en estado para que NO se
  // vuelva a evaluar la guarda al pasar por el modal de pausa olvidada. Sin
  // esto, la hora corregida se perdía y se reabría el modal de olvido en bucle.
  const [salidaOverride, setSalidaOverride] = useState(null) // hora 'HH:mm:ss' confirmada, o null
  const [olvidoResuelto, setOlvidoResuelto] = useState(false) // true una vez el usuario pasó el modal >14h

  const [modalHoraExtra, setModalHoraExtra] = useState(null)

  const [modalPausaOlvidada, setModalPausaOlvidada] = useState(false)
  const [pausaOlvidadaForm, setPausaOlvidadaForm] = useState({ hora_inicio: '', hora_fin: '', tipo: 'comida' })
  const [modoPausaOlvidada, setModoPausaOlvidada] = useState(null)

  // Sprint fichajes: aviso corrección + validación semanal + nueva captura geo
  const [avisoCorreccion, setAvisoCorreccion] = useState(null) // { aviso, fichaje } o null
  const [mostrarValidacionSemanal, setMostrarValidacionSemanal] = useState(false)
  const [sinCalendario, setSinCalendario] = useState(false)
  // Solicitudes de corrección del propio empleado en estado pendiente.
  // Se muestran como banner persistente en la cabecera de la pantalla para
  // dar feedback claro de que su corrección está esperando aprobación admin.
  const [correccionesPendientes, setCorreccionesPendientes] = useState([])
  // Permiso/ausencia aprobada que cubre el día de hoy. Si existe, el empleado
  // puede fichar igualmente (B5): mostramos un aviso no bloqueante.
  const [permisoHoy, setPermisoHoy] = useState(null)
  const { capturar: capturarGeoNueva } = useGeoFichaje()
  const { pendientes: validacionesPendientes } = useValidacionesPendientes()

  // Auto-mostrar modal de validación semanal si hay pendientes y nunca se cerró este sesión
  useEffect(() => {
    if (validacionesPendientes.length > 0 && !sessionStorage.getItem('validacion_semanal_dismissed')) {
      setMostrarValidacionSemanal(true)
    }
  }, [validacionesPendientes.length])

  const detectarYMostrarAvisoCorreccion = (data) => {
    if (!data?._avisos_calendario) return
    const aviso = data._avisos_calendario.find(a => a.tipo === 'fuera_horario_entrada' || a.tipo === 'fuera_horario_salida')
    if (!aviso) return
    setAvisoCorreccion({ aviso, fichaje: data })
  }

  const cargarCorreccionesPendientes = async () => {
    try {
      const r = await fetchConAuth(`${API_URL}/correcciones/mis-solicitudes?estado=pendiente`)
      if (!r.ok) return
      const data = await r.json()
      setCorreccionesPendientes(Array.isArray(data) ? data : [])
    } catch {
      // Silencio: el banner es informativo, no bloqueante.
    }
  }

  // Detecta si el empleado tiene una ausencia aprobada que cubre hoy. No bloquea
  // el fichaje (B5): solo alimenta el aviso del banner y del toast al fichar.
  const cargarPermisoHoy = async () => {
    try {
      const anio = new Date().getFullYear()
      const hoy = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      const r = await fetchConAuth(`${API_URL}/ausencias/mis-ausencias?anio=${anio}&estado=aprobada`)
      if (!r.ok) return
      const data = await r.json()
      const permiso = (Array.isArray(data) ? data : []).find(
        a => a.estado === 'aprobada' && a.fecha_inicio <= hoy && hoy <= a.fecha_fin
      )
      setPermisoHoy(permiso || null)
    } catch {
      // Silencio: el aviso es informativo, no bloqueante.
    }
  }

  const currentPlan = getEffectivePlan(empresaInfo)

  useEffect(() => {
    cargarTiposPausa()
    cargarFichajeActivo()
    cargarFichajesDelDia()
    cargarCorreccionesPendientes()
    cargarPermisoHoy()
    if (canAccess(currentPlan, 'geolocalizacion')) obtenerGeolocalizacion()
    const interval = setInterval(() => setHoraActual(new Date()), 1000)
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    let fechaActual = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
    const checkDayChange = setInterval(() => {
      const nuevaFecha = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      if (nuevaFecha !== fechaActual) {
        fechaActual = nuevaFecha
        cargarFichajeActivo()
        cargarFichajesDelDia()
      }
    }, 60000)
    return () => clearInterval(checkDayChange)
  }, [])

  useEffect(() => {
    if (!ultimaPausaCreada) {
      setSegundosRestantes(0)
      return
    }
    const calcular = () => {
      const transcurridos = Math.floor((Date.now() - ultimaPausaCreada.timestamp) / 1000)
      const restantes = Math.max(0, 30 - transcurridos)
      setSegundosRestantes(restantes)
      if (restantes === 0) setUltimaPausaCreada(null)
    }
    calcular()
    const interval = setInterval(calcular, 1000)
    return () => clearInterval(interval)
  }, [ultimaPausaCreada])

  const obtenerGeolocalizacion = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => setGeolocalizacion({ lat: position.coords.latitude, lng: position.coords.longitude }),
        (error) => console.error('Error obteniendo geolocalización:', error)
      )
    }
  }

  const obtenerIP = async () => {
    try {
      const response = await fetch('https://api.ipify.org?format=json')
      const data = await response.json()
      return data.ip
    } catch {
      return null
    }
  }

  const cargarFichajeActivo = async () => {
    try {
      const response = await fetchConAuth(`${API_URL}/fichajes/activo`, { method: 'GET' })
      if (response.ok) {
        const data = await response.json()
        if (data) {
          setFichajeActivo(data)
          setPausas(data.pausas || [])
          setPausaActiva(data.pausaActiva || null)
          const hoyLocal = new Date().toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
          if (data.fecha && data.fecha < hoyLocal) {
            setHoraOlvidoInput('18:00')
            setModalOlvido({ tipo: 'login', fichaje: data })
          }
        } else {
          setFichajeActivo(null)
          setPausas([])
          setPausaActiva(null)
        }
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) toast.error('Error al cargar fichaje')
    } finally {
      setLoading(false)
    }
  }

  const cargarFichajesDelDia = async () => {
    try {
      const ahora = new Date()
      const mes = (ahora.getMonth() + 1).toString().padStart(2, '0')
      const anio = ahora.getFullYear()
      const hoy = ahora.toLocaleDateString('en-CA', { timeZone: 'Europe/Madrid' })
      const response = await fetchConAuth(`${API_URL}/fichajes/historial?mes=${mes}&anio=${anio}`, { method: 'GET' })
      if (response.ok) {
        const data = await response.json()
        const fichajesHoy = data.fichajes.filter(f => f.fecha === hoy && f.hora_salida)
        setFichajesDelDia(fichajesHoy)
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) console.error('Error al cargar fichajes del día:', error)
    }
  }

  const cargarTiposPausa = async () => {
    try {
      const response = await tiposPausaAPI.getAll()
      setTiposPausa(response.data || [])
    } catch {
      toast.error('Error al cargar tipos de pausa')
    }
  }

  const calcularTiempoTranscurrido = (horaEntrada, tiempoPausas = 0, fechaFichaje = null) => {
    const [eh, em, es = 0] = horaEntrada.split(':').map(Number)
    if (isNaN(eh) || isNaN(em)) return { horas: 0, minutos: 0 }
    const ahora = new Date()
    let entrada
    if (fechaFichaje) {
      const [a, m, d] = fechaFichaje.split('-').map(Number)
      entrada = new Date(a, m - 1, d, eh, em, es)
    } else {
      entrada = new Date()
      entrada.setHours(eh, em, es, 0)
      if (entrada > ahora) entrada.setDate(entrada.getDate() - 1)
    }
    const minutosTranscurridos = (ahora - entrada) / (1000 * 60)
    const totalMinutos = Math.floor(minutosTranscurridos) - (tiempoPausas || 0)
    return { horas: Math.max(0, Math.floor(totalMinutos / 60)), minutos: Math.max(0, totalMinutos % 60) }
  }

  const calcularTiempoPausaActiva = () => {
    if (!pausaActiva) return { horas: 0, minutos: 0 }
    const ahora = new Date()
    const [ih, im, is = 0] = pausaActiva.hora_inicio.split(':').map(Number)
    let inicio
    if (fichajeActivo?.fecha) {
      const [a, m, d] = fichajeActivo.fecha.split('-').map(Number)
      inicio = new Date(a, m - 1, d, ih, im, is)
    } else {
      inicio = new Date()
      inicio.setHours(ih, im, is, 0)
      if (inicio > ahora) inicio.setDate(inicio.getDate() - 1)
    }
    const totalMinutos = Math.floor((ahora - inicio) / (1000 * 60))
    return { horas: Math.max(0, Math.floor(totalMinutos / 60)), minutos: Math.max(0, totalMinutos % 60) }
  }

  const ficharEntrada = async () => {
    setProcesando(true)
    try {
      const ip = await obtenerIP()
      const coords = await capturarGeoNueva()
      const response = await fetchConAuth(`${API_URL}/fichajes/entrada`, {
        method: 'POST',
        body: JSON.stringify({
          ip,
          geolocalizacion: geolocalizacion ? JSON.stringify(geolocalizacion) : null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          precision_m: coords?.precision_m ?? null
        })
      }, { reintentar: true })
      if (response.ok) {
        const data = await response.json()
        setFichajeActivo(data)
        setPausas([])
        setPausaActiva(null)
        toast.success('Entrada fichada correctamente')
        if (permisoHoy) {
          const etiqueta = TIPO_AUSENCIA_LABEL[permisoHoy.tipo] || 'Permiso'
          toast(`Has fichado teniendo un permiso aprobado para hoy (${etiqueta}). Quedará registrado y tu administrador lo verá.`, { icon: '⚠️', duration: 7000 })
        }
        detectarYMostrarAvisoCorreccion(data)
      } else if (response.status === 422) {
        const error = await response.json()
        if (error.codigo === 'SIN_CALENDARIO') {
          setSinCalendario(true)
          toast.error(error.error, { duration: 12000, icon: '📅' })
        } else {
          toast.error(error.error || 'La empresa requiere geolocalización', { duration: 8000 })
        }
      } else {
        const error = await response.json()
        // Si el backend dice que ya hay un fichaje activo, lo más probable es que
        // este POST sea un reintento de red de una entrada que SÍ se registró
        // (o un doble clic). En vez de un error alarmante, recargamos el estado.
        if (error.codigo === 'FICHAJE_ACTIVO') {
          await cargarFichajeActivo()
          toast('Ya tenías una entrada registrada.', { icon: 'ℹ️', duration: 4000 })
        } else {
          toast.error(error.error || 'Error al fichar entrada', { duration: 6000 })
        }
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) {
        toast.error('Error de conexión al fichar entrada. Comprueba tu conexión a internet.', { duration: 8000 })
      }
    } finally {
      setProcesando(false)
    }
  }

  const iniciarPausa = async (tipoPausa) => {
    setMostrarModalPausa(false)
    setProcesando(true)
    try {
      const ip = await obtenerIP()
      const coords = await capturarGeoNueva()
      const response = await fetchConAuth(`${API_URL}/fichajes/pausar`, {
        method: 'POST',
        body: JSON.stringify({
          tipo: tipoPausa, ip,
          geolocalizacion: geolocalizacion ? JSON.stringify(geolocalizacion) : null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          precision_m: coords?.precision_m ?? null
        })
      })
      if (response.ok) {
        const data = await response.json()
        setPausaActiva(data.pausa)
        setPausas([...pausas, data.pausa])
        setFichajeActivo(prev => ({ ...prev, estado: 'pausado' }))
        setUltimaPausaCreada({ id: data.pausa.id, timestamp: Date.now() })
        toast.success('Pausa iniciada')
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al iniciar pausa', { duration: 6000 })
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) toast.error('Error de conexión al iniciar pausa', { duration: 6000 })
    } finally {
      setProcesando(false)
    }
  }

  const reanudarFichaje = async () => {
    setProcesando(true)
    try {
      const ip = await obtenerIP()
      const coords = await capturarGeoNueva()
      const response = await fetchConAuth(`${API_URL}/fichajes/reanudar`, {
        method: 'POST',
        body: JSON.stringify({
          ip,
          geolocalizacion: geolocalizacion ? JSON.stringify(geolocalizacion) : null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          precision_m: coords?.precision_m ?? null
        })
      })
      if (response.ok) {
        const data = await response.json()
        setPausaActiva(null)
        setPausas(data.todas_las_pausas || [])
        setFichajeActivo(prev => ({
          ...prev,
          estado: 'activo',
          tiempo_pausas: (prev.tiempo_pausas || 0) + data.pausa_cerrada.duracion_minutos
        }))
        toast.success('Fichaje reanudado')
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al reanudar', { duration: 6000 })
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) toast.error('Error de conexión al reanudar', { duration: 6000 })
    } finally {
      setProcesando(false)
    }
  }

  const eliminarPausa = async (pausaId) => {
    setProcesando(true)
    try {
      const ip = await obtenerIP()
      const response = await fetchConAuth(`${API_URL}/fichajes/pausar/${pausaId}`, {
        method: 'DELETE',
        body: JSON.stringify({ ip })
      })
      if (response.ok) {
        setPausas(pausas.filter(p => p.id !== pausaId))
        setPausaActiva(null)
        setUltimaPausaCreada(null)
        setFichajeActivo(prev => ({ ...prev, estado: 'activo' }))
        toast.success('Pausa eliminada')
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al eliminar pausa', { duration: 6000 })
      }
    } catch (error) {
      if (!error.message.includes('Sesión expirada')) toast.error('Error de conexión al eliminar pausa', { duration: 6000 })
    } finally {
      setProcesando(false)
    }
  }

  const ficharSalida = async (horaOverride = null, pausaOlvidada = null) => {
    setMostrarModalSalida(false)
    setProcesandoOlvido(false)
    setModalPausaOlvidada(false)

    // Override efectivo: el pasado como argumento o el ya confirmado en estado
    // (cuando venimos del modal de olvido y luego pasamos por el de pausa).
    const overrideEfectivo = horaOverride || salidaOverride

    // Guarda >14h: solo si el usuario aún no ha resuelto el modal de olvido.
    // `olvidoResuelto` evita el bucle olvido ↔ pausa al no reevaluar la guarda.
    if (!olvidoResuelto && !overrideEfectivo && fichajeActivo && tiempoTranscurrido > 840) {
      setHoraOlvidoInput('18:00')
      setModalOlvido({ tipo: 'salida', fichaje: fichajeActivo })
      return
    }

    const pausasCompletadas = pausas.filter(p => p.hora_fin)
    if (!pausaOlvidada && pausaOlvidada !== false && fichajeActivo &&
        tiempoTranscurrido > 360 && pausasCompletadas.length === 0 && !estaPausado) {
      const [eh, em] = fichajeActivo.hora_entrada.split(':').map(Number)
      const mediaTurnoMin = eh * 60 + em + Math.floor(tiempoTranscurrido / 2)
      const hSug = String(Math.floor(mediaTurnoMin / 60) % 24).padStart(2, '0')
      const mSug = String(mediaTurnoMin % 60).padStart(2, '0')
      const finSug = String(Math.floor((mediaTurnoMin + 30) / 60) % 24).padStart(2, '0')
      const mFinSug = String((mediaTurnoMin + 30) % 60).padStart(2, '0')
      setPausaOlvidadaForm({ hora_inicio: `${hSug}:${mSug}`, hora_fin: `${finSug}:${mFinSug}`, tipo: 'comida' })
      setModoPausaOlvidada(null)
      setModalPausaOlvidada(true)
      return
    }

    setProcesando(true)
    try {
      const ip = await obtenerIP()
      const coords = await capturarGeoNueva()
      const response = await fetchConAuth(`${API_URL}/fichajes/salida`, {
        method: 'POST',
        body: JSON.stringify({
          ip, geolocalizacion: geolocalizacion ? JSON.stringify(geolocalizacion) : null,
          lat: coords?.lat ?? null,
          lng: coords?.lng ?? null,
          precision_m: coords?.precision_m ?? null,
          confirmado: true,
          ...(overrideEfectivo && { hora_salida_override: overrideEfectivo }),
          ...(pausaOlvidada && { pausa_olvidada: pausaOlvidada })
        })
      }, { reintentar: true })

      if (response.ok) {
        const data = await response.json()
        setFichajeActivo(null)
        setPausas([])
        setPausaActiva(null)
        setSalidaOverride(null)
        setOlvidoResuelto(false)
        await cargarFichajesDelDia()
        toast.success('Salida fichada correctamente')

        const tiempoEfectivo = data.tiempo_efectivo || (data.tiempo_total - (data.tiempo_pausas || 0))
        const horas = Math.floor(tiempoEfectivo / 60)
        const minutos = tiempoEfectivo % 60
        const horasPausas = Math.floor((data.tiempo_pausas || 0) / 60)
        const minutosPausas = (data.tiempo_pausas || 0) % 60
        if (data.tiempo_pausas > 0) {
          toast.success(`Tiempo efectivo: ${horas}h ${minutos}m (Pausas: ${horasPausas}h ${minutosPausas}m)`, { duration: 5000 })
        } else {
          toast.success(`Tiempo trabajado: ${horas}h ${minutos}m`)
        }

        if (data.id) {
          try {
            const checkRes = await fetchConAuth(`${API_URL}/horas-extras/check/${data.id}`)
            if (checkRes.ok) {
              const checkData = await checkRes.json()
              if (checkData.mostrar && !checkData.ya_solicitada) {
                setModalHoraExtra({
                  fichaje_id: data.id,
                  minutosExtra: checkData.minutos_extra,
                  minutosTurno: checkData.minutos_turno
                })
              }
            }
          } catch { /* ignore */ }
        }

        detectarYMostrarAvisoCorreccion(data)
      } else if (response.status === 422) {
        const error = await response.json()
        toast.error(error.error || 'La empresa requiere geolocalización', { duration: 8000 })
        await cargarFichajeActivo()
      } else {
        const error = await response.json()
        toast.error(error.error || 'Error al fichar salida', { duration: 6000 })
        await cargarFichajeActivo()
      }
    } catch (error) {
      if (error.message?.includes('Sesión expirada')) {
        setProcesando(false)
        return
      }
      // Verificar si la salida sí se registró pese al error de red.
      // Si ya no hay fichaje activo significa que el backend procesó la salida.
      let salidaConfirmada = false
      try {
        const verify = await fetchConAuth(`${API_URL}/fichajes/activo`, { method: 'GET' })
        if (verify.ok) {
          const activo = await verify.json()
          salidaConfirmada = !activo || !activo.id
        }
      } catch { /* ignore */ }

      if (salidaConfirmada) {
        setFichajeActivo(null)
        setPausas([])
        setPausaActiva(null)
        setSalidaOverride(null)
        setOlvidoResuelto(false)
        await cargarFichajesDelDia()
        toast.success('Salida registrada (verificada tras un fallo de red).', { duration: 6000 })
      } else {
        toast.error(
          'Error de conexión al fichar salida. Tu salida puede NO haberse registrado. Comprueba tu conexión e inténtalo de nuevo.',
          { duration: 10000, icon: '⚠️' }
        )
        try { await cargarFichajeActivo() } catch { /* ignore */ }
      }
    } finally {
      setProcesando(false)
    }
  }

  const confirmarOlvido = async () => {
    if (!horaOlvidoInput) { toast.error('Introduce la hora de salida'); return }
    const [h, m] = horaOlvidoInput.split(':').map(Number)
    if (isNaN(h) || isNaN(m) || h < 0 || h > 23 || m < 0 || m > 59) {
      toast.error('Hora no válida'); return
    }
    const horaFormateada = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`
    setProcesandoOlvido(true)
    setModalOlvido(null)
    // Persistir la decisión para que la guarda >14h no se reevalúe si el flujo
    // pasa luego por el modal de pausa olvidada.
    setSalidaOverride(horaFormateada)
    setOlvidoResuelto(true)
    await ficharSalida(horaFormateada)
    setProcesandoOlvido(false)
  }

  const formatearHora = (date) =>
    date.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  const formatearDuracion = (minutos) => {
    if (!minutos) return '0m'
    const h = Math.floor(minutos / 60), m = minutos % 60
    if (h > 0) return `${h}h ${m}m`
    return `${m}m`
  }

  const getTipoPausaInfo = (tipoCodigo) => {
    const tipo = tiposPausa.find(t => t.codigo === tipoCodigo)
    if (!tipo) return { codigo: tipoCodigo, nombre: tipoCodigo, icono: FiClock }
    const IconComponent = FiIcons[tipo.icono] || FiClock
    return { ...tipo, id: tipo.codigo, icono: IconComponent }
  }

  const tiempoTranscurridoObj = fichajeActivo
    ? calcularTiempoTranscurrido(fichajeActivo.hora_entrada, fichajeActivo.tiempo_pausas || 0, fichajeActivo.fecha)
    : null
  const tiempoTranscurrido = tiempoTranscurridoObj ? tiempoTranscurridoObj.horas * 60 + tiempoTranscurridoObj.minutos : 0
  const tiempoPausaActual = calcularTiempoPausaActiva()
  const estaPausado = fichajeActivo?.estado === 'pausado' || pausaActiva
  const totalMinutosPausas = pausas.filter(p => p.duracion_minutos).reduce((acc, p) => acc + p.duracion_minutos, 0)

  const calcularTiempoTotalDia = () => {
    const tiempoFichajesCompletados = fichajesDelDia
      .filter(f => f.hora_salida && f.id !== fichajeActivo?.id)
      .reduce((total, fichaje) => {
        const fechaBase = fichaje.fecha || '2000-01-01'
        const entrada = new Date(`${fechaBase}T${fichaje.hora_entrada}`)
        let salida = new Date(`${fechaBase}T${fichaje.hora_salida}`)
        if (salida < entrada) salida.setDate(salida.getDate() + 1)
        const minutosTranscurridos = Math.floor((salida - entrada) / (1000 * 60))
        return total + (minutosTranscurridos - (fichaje.tiempo_pausas || 0))
      }, 0)
    const tiempoFichajeActual = tiempoTranscurridoObj
      ? tiempoTranscurridoObj.horas * 60 + tiempoTranscurridoObj.minutos
      : 0
    const totalMinutos = tiempoFichajesCompletados + tiempoFichajeActual
    return {
      horas: Math.floor(totalMinutos / 60),
      minutos: totalMinutos % 60,
      tiempoFichajesAnteriores: tiempoFichajesCompletados,
      tiempoFichajeActual
    }
  }

  const tiempoTotalDia = calcularTiempoTotalDia()

  // Estado del fichaje para PageHeader sub
  const subInfo = !fichajeActivo
    ? 'Pulsa "Fichar entrada" para iniciar tu jornada'
    : estaPausado
    ? 'Pausa activa — pulsa "Reanudar" cuando vuelvas'
    : `Activo desde las ${fichajeActivo.hora_entrada?.substring(0, 5)}`

  return (
    <>
      <PageHeader
        num="§ M1"
        kicker="Mi jornada"
        title="Fichaje en curso."
        italic="Tu reloj de trabajo."
        sub={subInfo}
      />

      <div className="px-7 py-6 space-y-6">
        {sinCalendario && (
          <div className="ui-card p-4 border-l-4 border-l-[#A86A06] bg-[rgba(245,158,11,.05)]">
            <div className="flex items-start gap-3">
              <span className="text-[20px]">📅</span>
              <div className="flex-1">
                <div className="font-display text-[14px] text-[var(--ink)]">No tienes calendario laboral asignado</div>
                <p className="text-[12.5px] text-[var(--ink-2)] mt-1">
                  No puedes fichar mientras no tengas un calendario asignado (propio o a través de tu grupo).
                  Contacta con un administrador para que te lo asigne; de lo contrario el cálculo de jornada y de horas extras no funcionaría correctamente.
                </p>
              </div>
            </div>
          </div>
        )}

        {correccionesPendientes.length > 0 && (
          <div className="ui-card p-4 border-l-4 border-l-[#A86A06] bg-[rgba(245,158,11,.05)]">
            <div className="flex items-start gap-3">
              <FiIcons.FiClock size={18} className="text-[#A86A06] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-display text-[14px] text-[var(--ink)]">
                  {correccionesPendientes.length === 1
                    ? 'Tienes una solicitud de corrección pendiente de aprobación'
                    : `Tienes ${correccionesPendientes.length} solicitudes de corrección pendientes de aprobación`}
                </div>
                <p className="text-[12.5px] text-[var(--ink-2)] mt-1">
                  Tu fichaje seguirá registrado tal cual hasta que un administrador apruebe la solicitud. Te avisaremos cuando se resuelva.
                </p>
                <ul className="mt-3 space-y-1 text-[12px] text-[var(--ink-2)]">
                  {correccionesPendientes.slice(0, 3).map((c) => {
                    const correc = c.hora_entrada_corregida || c.hora_salida_corregida
                    const orig = c.hora_entrada_original || c.hora_salida_original
                    return (
                      <li key={c.id} className="font-mono tnum">
                        · {c.fecha} — solicitas <b>{correc}</b>
                        {orig ? <span className="text-[var(--ink-3)]"> (registrado: {orig})</span> : null}
                      </li>
                    )
                  })}
                  {correccionesPendientes.length > 3 && (
                    <li className="text-[var(--ink-3)] italic">
                      … y {correccionesPendientes.length - 3} más
                    </li>
                  )}
                </ul>
              </div>
            </div>
          </div>
        )}

        {permisoHoy && (
          <div className="ui-card p-4 border-l-4 border-l-[#7C3AED] bg-[rgba(124,58,237,.05)]">
            <div className="flex items-start gap-3">
              <FiIcons.FiCalendar size={18} className="text-[#7C3AED] mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <div className="font-display text-[14px] text-[var(--ink)]">
                  Tienes un permiso aprobado para hoy ({TIPO_AUSENCIA_LABEL[permisoHoy.tipo] || 'Permiso'})
                </div>
                <p className="text-[12.5px] text-[var(--ink-2)] mt-1">
                  Puedes fichar igualmente si vas a trabajar. Si lo haces, tu jornada quedará registrada y tu administrador
                  la verá como «trabajando con permiso aprobado».
                </p>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="ui-card p-8">
            <div className="space-y-4">
              <div className="h-6 w-1/3 bg-[var(--bg-2)] animate-pulse rounded-soft" />
              <div className="h-24 w-full bg-[var(--bg-2)] animate-pulse rounded-soft" />
              <div className="h-12 w-full bg-[var(--bg-2)] animate-pulse rounded-soft" />
            </div>
          </div>
        ) : (
          <TimelineFichajeV2
            horaActual={horaActual}
            fichajeActivo={fichajeActivo}
            pausas={pausas}
            pausaActiva={pausaActiva}
            estaPausado={estaPausado}
            tiempoTranscurrido={tiempoTranscurridoObj}
            tiempoTotalDia={tiempoTotalDia}
            tiempoPausaActual={tiempoPausaActual}
            totalMinutosPausas={totalMinutosPausas}
            getTipoPausaInfo={getTipoPausaInfo}
            formatearDuracion={formatearDuracion}
            fichajesDelDia={fichajesDelDia}
            geolocalizacion={geolocalizacion}
            procesando={procesando}
            onFicharEntrada={ficharEntrada}
            onFicharSalida={() => { setSalidaOverride(null); setOlvidoResuelto(false); setMostrarModalSalida(true) }}
            onPausar={() => setMostrarModalPausa(true)}
            onReanudar={reanudarFichaje}
            ultimaPausaCreada={ultimaPausaCreada}
            segundosRestantes={segundosRestantes}
            onEliminarPausa={eliminarPausa}
            empleado={empleado}
            empresaInfo={empresaInfo}
          />
        )}
      </div>

      {/* ── Modal: Confirmar salida ── */}
      {mostrarModalSalida && fichajeActivo && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <div className="eyebrow mb-2">Confirmar fichaje</div>
              <h3 className="font-display text-[24px] text-[var(--ink)]">Cierre de jornada.</h3>
            </div>
            <div className="px-6 pb-5 space-y-3 text-[13px]">
              <div className="flex justify-between">
                <span className="text-[var(--ink-3)]">Hora de entrada</span>
                <span className="font-mono tnum text-[var(--ink)]">{fichajeActivo.hora_entrada}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--ink-3)]">Hora de salida</span>
                <span className="font-mono tnum text-[var(--ink)]">{formatearHora(new Date())}</span>
              </div>
              {pausas.length > 0 && (
                <>
                  <div className="border-t border-[var(--line)] pt-3">
                    <p className="eyebrow mb-2">Pausas realizadas</p>
                    {pausas.map((pausa, i) => {
                      const tp = getTipoPausaInfo(pausa.tipo)
                      return (
                        <div key={pausa.id || i} className="flex justify-between text-[12px] text-[var(--ink-3)] mb-1">
                          <span>{tp.nombre} · {pausa.hora_inicio} – {pausa.hora_fin || 'activa'}</span>
                          {pausa.duracion_minutos && <span className="font-mono tnum">{formatearDuracion(pausa.duracion_minutos)}</span>}
                        </div>
                      )
                    })}
                  </div>
                  <div className="flex justify-between border-t border-[var(--line)] pt-3">
                    <span className="text-[var(--ink-3)]">Total pausas</span>
                    <span className="font-mono tnum text-[var(--ink)]">{formatearDuracion(totalMinutosPausas + (pausaActiva ? tiempoPausaActual.horas * 60 + tiempoPausaActual.minutos : 0))}</span>
                  </div>
                </>
              )}
              <div className="flex justify-between border-t border-[var(--line)] pt-3 font-medium">
                <span className="text-[var(--ink)]">Tiempo efectivo estimado</span>
                <span className="font-mono tnum text-[#0F8C66]">
                  {(() => {
                    const t = calcularTiempoTranscurrido(
                      fichajeActivo.hora_entrada,
                      (fichajeActivo.tiempo_pausas || 0) + (pausaActiva ? tiempoPausaActual.horas * 60 + tiempoPausaActual.minutos : 0),
                      fichajeActivo.fecha
                    )
                    return `${t.horas}h ${t.minutos}m`
                  })()}
                </span>
              </div>
              {pausaActiva && (
                <div className="px-3 py-2 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.2)] text-[11.5px] text-[#A86A06]">
                  La pausa activa se cerrará automáticamente al fichar la salida.
                </div>
              )}
              <div className="px-3 py-2 rounded-soft bg-[var(--bg-2)] border border-[var(--line)] text-[11px] text-[var(--ink-3)]">
                Al confirmar, certifico que los datos de esta jornada son correctos conforme al registro horario obligatorio (RD-Ley 8/2019).
              </div>
            </div>
            <div className="px-6 pb-6 flex gap-3">
              <Btn variant="ghost" className="flex-1" onClick={() => setMostrarModalSalida(false)}>
                Cancelar
              </Btn>
              <Btn variant="primary" className="flex-1" onClick={() => ficharSalida()} disabled={procesando}>
                {procesando ? 'Procesando…' : 'Confirmar salida'}
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Tipo de pausa ── */}
      {mostrarModalPausa && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-md">
            <div className="px-6 pt-6 pb-3">
              <div className="eyebrow mb-2">Pausa</div>
              <h3 className="font-display text-[24px] text-[var(--ink)]">Selecciona el tipo.</h3>
            </div>
            <div className="px-6 pb-5">
              {tiposPausa.length === 0 ? (
                <p className="text-[13px] text-[var(--ink-3)] text-center py-4">
                  No hay tipos de pausa configurados
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {tiposPausa.map((tipo) => {
                    const tipoInfo = getTipoPausaInfo(tipo.codigo)
                    const Icono = tipoInfo.icono
                    return (
                      <button
                        key={tipo.id}
                        type="button"
                        onClick={() => iniciarPausa(tipo.codigo)}
                        disabled={procesando}
                        className="p-4 rounded-soft border border-[var(--line)] hover:border-[var(--ink-3)] hover:bg-[var(--bg-2)] transition-colors text-left disabled:opacity-50"
                      >
                        <Icono className="h-6 w-6 mx-auto mb-2 text-[var(--ink-2)]" />
                        <span className="block text-center text-[13px] font-medium">{tipo.nombre}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
            <div className="px-6 pb-6">
              <Btn variant="ghost" className="w-full" onClick={() => setMostrarModalPausa(false)}>
                Cancelar
              </Btn>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal: Recordatorio de pausa olvidada ── */}
      {modalPausaOlvidada && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-sm">
            <div className="px-6 pt-6 pb-3 text-center">
              <div className="w-14 h-14 rounded-full bg-[rgba(245,158,11,.10)] flex items-center justify-center mx-auto mb-3">
                <FiClock className="h-7 w-7 text-[#A86A06]" />
              </div>
              <h3 className="font-display text-[22px] text-[var(--ink)]">¿Has hecho alguna pausa?</h3>
              <p className="text-[12.5px] text-[var(--ink-3)] mt-1.5">
                Llevas más de 6 horas trabajando sin registrar ninguna pausa.
              </p>
            </div>

            {modoPausaOlvidada === null && (
              <div className="px-6 pb-6 flex flex-col gap-2.5">
                <Btn variant="primary" className="w-full" onClick={() => setModoPausaOlvidada('añadir')}>
                  Añadir pausa olvidada
                </Btn>
                <Btn variant="ghost" className="w-full" onClick={() => ficharSalida(null, false)}>
                  No he hecho pausa, fichar salida
                </Btn>
                <button
                  type="button"
                  onClick={() => setModalPausaOlvidada(false)}
                  className="text-[11.5px] text-[var(--ink-3)] hover:text-[var(--ink)] mt-1"
                >
                  Cancelar
                </button>
              </div>
            )}

            {modoPausaOlvidada === 'añadir' && (
              <div className="px-6 pb-6">
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <label className="eyebrow block mb-1.5">Inicio pausa *</label>
                    <input
                      type="time"
                      value={pausaOlvidadaForm.hora_inicio}
                      onChange={e => setPausaOlvidadaForm(f => ({ ...f, hora_inicio: e.target.value }))}
                      className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-center font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                    />
                  </div>
                  <div>
                    <label className="eyebrow block mb-1.5">Fin pausa *</label>
                    <input
                      type="time"
                      value={pausaOlvidadaForm.hora_fin}
                      onChange={e => setPausaOlvidadaForm(f => ({ ...f, hora_fin: e.target.value }))}
                      className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-center font-mono text-[14px] focus:outline-none focus:border-[var(--ink-3)]"
                    />
                  </div>
                </div>

                <div className="mb-5">
                  <label className="eyebrow block mb-1.5">Tipo de pausa</label>
                  <select
                    value={pausaOlvidadaForm.tipo}
                    onChange={e => setPausaOlvidadaForm(f => ({ ...f, tipo: e.target.value }))}
                    className="w-full px-3 py-2 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-[13px] focus:outline-none focus:border-[var(--ink-3)]"
                  >
                    {(tiposPausa.length > 0 ? tiposPausa : [
                      { codigo: 'comida', nombre: 'Comida' },
                      { codigo: 'descanso', nombre: 'Descanso' },
                      { codigo: 'medico', nombre: 'Médico' },
                      { codigo: 'personal', nombre: 'Personal' },
                      { codigo: 'otro', nombre: 'Otro' }
                    ]).map(t => (
                      <option key={t.codigo || t.id} value={t.codigo || t.id}>{t.nombre}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-3">
                  <Btn variant="ghost" className="flex-1" onClick={() => setModoPausaOlvidada(null)}>
                    Atrás
                  </Btn>
                  <Btn
                    variant="primary"
                    className="flex-1"
                    disabled={procesando}
                    onClick={() => {
                      if (!pausaOlvidadaForm.hora_inicio || !pausaOlvidadaForm.hora_fin) {
                        toast.error('Indica la hora de inicio y fin de la pausa')
                        return
                      }
                      ficharSalida(null, pausaOlvidadaForm)
                    }}
                  >
                    {procesando ? 'Guardando…' : 'Guardar y fichar salida'}
                  </Btn>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal: Hora extra ── */}
      {modalHoraExtra && (
        <ModalHoraExtra
          fichajeId={modalHoraExtra.fichaje_id}
          minutosExtra={modalHoraExtra.minutosExtra}
          minutosTurno={modalHoraExtra.minutosTurno}
          onClose={() => setModalHoraExtra(null)}
          onSuccess={() => setModalHoraExtra(null)}
        />
      )}

      {/* ── Modal: Fichaje olvidado ── */}
      {modalOlvido && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="ui-card w-full max-w-sm">
            <div className="px-6 pt-6 pb-3 text-center">
              <div className="w-14 h-14 rounded-full bg-[rgba(245,158,11,.10)] flex items-center justify-center mx-auto mb-3">
                <FiClock className="h-7 w-7 text-[#A86A06]" />
              </div>
              <h3 className="font-display text-[22px] text-[var(--ink)]">
                {modalOlvido.tipo === 'login' ? '¿Olvidaste fichar la salida?' : 'Parece que olvidaste fichar la salida'}
              </h3>
              <p className="text-[12.5px] text-[var(--ink-3)] mt-1.5">
                {modalOlvido.tipo === 'login' ? (
                  <>Tienes una entrada abierta del <strong>{formatearFecha(modalOlvido.fichaje.fecha)}</strong> a las <strong>{modalOlvido.fichaje.hora_entrada}</strong>.</>
                ) : (
                  <>Llevas más de 14 horas fichado. Indica a qué hora saliste realmente.</>
                )}
              </p>
            </div>

            <div className="mx-6 mb-4 px-3 py-2.5 rounded-soft bg-[rgba(245,158,11,.06)] border border-[rgba(245,158,11,.2)] text-center text-[12.5px]">
              <span className="text-[var(--ink-3)]">Entrada: </span>
              <span className="font-mono tnum font-medium text-[var(--ink)]">{modalOlvido.fichaje.hora_entrada}</span>
              {modalOlvido.tipo === 'login' && (
                <span className="text-[var(--ink-3)] ml-2 font-mono">({modalOlvido.fichaje.fecha})</span>
              )}
            </div>

            <div className="px-6 mb-5">
              <label className="eyebrow block mb-2 text-center">¿A qué hora saliste?</label>
              <input
                type="time"
                value={horaOlvidoInput}
                onChange={e => setHoraOlvidoInput(e.target.value)}
                className="w-full px-3 py-2.5 bg-[var(--bg-2)] border border-[var(--line)] rounded-soft text-center text-[22px] font-mono tnum focus:outline-none focus:border-[var(--ink-3)]"
                autoFocus
              />
            </div>

            <div className="px-6 pb-6">
              <div className="flex gap-3">
                <Btn
                  variant="ghost"
                  className="flex-1"
                  onClick={() => {
                    if (modalOlvido.tipo === 'salida') {
                      // "Cerrar ahora": el empleado confirma que trabajó >14h de
                      // verdad. Cerramos a la hora actual (sin override) saltando
                      // la guarda de olvido.
                      setModalOlvido(null)
                      setOlvidoResuelto(true)
                      ficharSalida(null)
                    } else {
                      setModalOlvido(null)
                    }
                  }}
                  disabled={procesandoOlvido}
                >
                  {modalOlvido.tipo === 'login' ? 'Lo gestiona el admin' : 'Cerrar ahora'}
                </Btn>
                <Btn
                  variant="primary"
                  className="flex-1"
                  onClick={confirmarOlvido}
                  disabled={procesandoOlvido || !horaOlvidoInput}
                >
                  {procesandoOlvido ? 'Guardando…' : 'Guardar salida'}
                </Btn>
              </div>
              {modalOlvido.tipo === 'salida' && (
                <p className="text-[11px] text-[var(--ink-3)] text-center mt-3">
                  Si realmente trabajaste más de 14h, pulsa "Cerrar ahora"
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Aviso de corrección al fichar fuera de ventana del calendario */}
      {avisoCorreccion && (
        <ModalAvisoCorreccionV2
          aviso={avisoCorreccion.aviso}
          fichaje={avisoCorreccion.fichaje}
          onClose={() => {
            setAvisoCorreccion(null)
            // Tras cerrar el modal, refrescamos pendientes para que el banner
            // refleje inmediatamente la solicitud recién enviada.
            cargarCorreccionesPendientes()
          }}
        />
      )}

      {/* Validación semanal de fichajes grupales (auto al login si hay pendientes) */}
      {mostrarValidacionSemanal && (
        <ModalValidacionSemanalV2
          onClose={() => {
            sessionStorage.setItem('validacion_semanal_dismissed', '1')
            setMostrarValidacionSemanal(false)
          }}
        />
      )}
    </>
  )
}

export default MiFichajeV2
